import classnames from 'classnames'
import * as React from 'react'
import { useMemo } from 'react'
import { Link, useLoaderData } from 'react-router-dom'

import { withQueryClientContext } from '../../../../app/shared/components/QueryClientContext'
import { Select } from '../../../components'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderSortable,
  TableRow,
  TableStickyColumnWrapper,
  useTableSortWithUrl,
} from '../../../components/Table'
import { Pagination } from '../../../components/pagination'
import { getStepPagination } from '../../../hooks/use-table-pagination'
import { CenteredAlert } from '../components/CenteredAlert'
import { ConfidentialWarning } from '../components/ConfidentialWarning'
import { ManagerDetailsLayout } from '../components/ManagerDetailsLayout'
import { ManagerHeading } from '../components/ManagerHeading'
import { NoOwnHoldingsWarnings } from '../components/NoOwnHoldingsWarnings'
import {
  HoldingColumnEnum,
  HoldingOptionType,
  HoldingOptionTypeLabelMap,
  HoldingOptionTypes,
  HoldingTransactionType,
  HoldingTransactionTypeLabelMap,
  HoldingTransactionTypes,
  InitialManagerHoldingsData,
  InsiderView,
  InsiderViewParam,
  InvestorType,
} from '../constants'
import { useInsiderTradingRouter } from '../hooks/useInsiderTradingRouter'
import { useManagerHoldingsData } from '../hooks/useManagerHoldingsData'
import { MANAGER_HOLDINGS_TABLE_COLUMNS } from '../managerHoldingsTableColumns'

const DEFAULT_SORT = `-${HoldingColumnEnum.marketValue}`

function ManagerHoldings() {
  const initialData = useLoaderData() as InitialManagerHoldingsData | null

  // This is temporary until select options are changed to use Link without reloadDocument
  const [tempOptionType, setTempOptionType] = React.useState<string | undefined>()
  const [tempTransactionType, setTempTransactionType] = React.useState<string | undefined>()
  const insiderTradingRouter = useInsiderTradingRouter<InsiderView.ManagerHoldings>()
  const { page: currentPage, sort = DEFAULT_SORT, reportEndDate } = insiderTradingRouter.viewParams

  const tableSortWithUrl = useTableSortWithUrl({
    data: [],
    columns: MANAGER_HOLDINGS_TABLE_COLUMNS,
    urlPersistKey: InsiderViewParam.Sort,
    defaultSort: sort as HoldingColumnEnum, // column id is not string, so we need to satisfy TS
  })

  const managerHoldingsData = useManagerHoldingsData({
    id: insiderTradingRouter.viewParams.investorId,
    initialData: initialData?.initialManagerHoldingsData,
    page: currentPage,
    sort,
    shouldResetScroll: true,
    reportEndDate,
  })

  // useRef is needed to cache certain data which comes from initialData and is not available after initial rerenders
  const { current: managerHoldingsFiler } = React.useRef(initialData?.initialManagerHoldingsFiler)
  const { current: reportEndDates } = React.useRef(initialData?.initialManagerReportDates ?? [])
  const { current: isConfidentialOmitted } = React.useRef(
    initialData?.initialLatestSummary?.isConfidentialOmitted ?? false
  )
  const totalPages = managerHoldingsData.data?.totalPages ?? 0
  const stepNavigation = useMemo(
    () => getStepPagination({ pages: totalPages, currentPage, maxSidePreviewPages: 3 }),
    [totalPages, currentPage]
  )

  const sortedData = managerHoldingsData.data?.items ?? []
  const isEmpty = sortedData.length === 0

  return (
    <ManagerDetailsLayout filer={managerHoldingsFiler} reportEndDates={reportEndDates} isContainer={false}>
      <div className="fv-container has-responsive-padding mb-7">
        <div className="flex items-baseline justify-between gap-4">
          <ManagerHeading filer={managerHoldingsFiler} />
          <div className="flex flex-none items-center space-x-4">
            <div className="flex items-center">
              <Select
                size="small"
                label={<span className="text-2xs text-muted">Transaction Type</span>}
                aria-label="Transaction Type"
                value={
                  tempTransactionType ?? insiderTradingRouter.viewParams.transactionType ?? HoldingTransactionType.Any
                }
                items={HoldingTransactionTypes.map((transactionType) => ({
                  value: transactionType,
                  label: HoldingTransactionTypeLabelMap[transactionType],
                  href: insiderTradingRouter.getInsiderTradingLink({
                    view: InsiderView.ManagerHoldings,
                    params: {
                      ...insiderTradingRouter.viewParams,
                      transactionType: transactionType === HoldingTransactionType.Any ? undefined : transactionType,
                    },
                  }),
                  reloadDocument: true,
                }))}
                className="ml-2 w-24"
                onChange={({ value }) => setTempTransactionType(value)}
              />
            </div>
            <div className="flex items-center">
              <Select
                size="small"
                label={<span className="text-2xs text-muted">Type</span>}
                aria-label="Type"
                value={tempOptionType ?? insiderTradingRouter.viewParams.optionType ?? HoldingOptionType.Any}
                items={HoldingOptionTypes.map((optionType) => ({
                  value: optionType,
                  label: HoldingOptionTypeLabelMap[optionType],
                  href: insiderTradingRouter.getInsiderTradingLink({
                    view: InsiderView.ManagerHoldings,
                    params: {
                      ...insiderTradingRouter.viewParams,
                      optionType: optionType === HoldingOptionType.Any ? undefined : optionType,
                    },
                  }),
                  reloadDocument: true,
                }))}
                className="ml-2 w-18"
                onChange={({ value }) => setTempOptionType(value)}
              />
            </div>
          </div>
        </div>
        {isConfidentialOmitted && <ConfidentialWarning />}
        {!managerHoldingsFiler?.hasOwnHoldings && managerHoldingsFiler?.investorType === InvestorType.OtherManager && (
          <NoOwnHoldingsWarnings
            filerName={managerHoldingsFiler.filerName}
            filerCik={managerHoldingsFiler.filerCik}
            otherManagerName={managerHoldingsFiler.otherManagerName}
          />
        )}
      </div>
      {isEmpty ? (
        <CenteredAlert>No results match your filters. Adjust them to explore other items.</CenteredAlert>
      ) : (
        <>
          <div className="fv-container has-responsive-padding is-full-width flex justify-center">
            <TableStickyColumnWrapper hasBorder>
              <Table className="table-fixed" isFullWidth={false}>
                <TableHead className="bg-primary">
                  {MANAGER_HOLDINGS_TABLE_COLUMNS.map((column) => {
                    const isSortingDisabled = column.id === HoldingColumnEnum.currentPrice
                    return (
                      <TableHeaderSortable
                        key={column.id}
                        align={column.align}
                        className={column.className}
                        isActive={column.id === tableSortWithUrl.sort.column?.id}
                        direction={tableSortWithUrl.sort.direction}
                        isSticky={column.id === HoldingColumnEnum.ticker}
                        onClick={
                          isSortingDisabled
                            ? undefined
                            : () =>
                                tableSortWithUrl.changeSort(
                                  column,
                                  new URLSearchParams({ [InsiderViewParam.Page]: '1' })
                                )
                        }
                        disabled={isSortingDisabled}
                      >
                        {column.name}
                      </TableHeaderSortable>
                    )
                  })}
                </TableHead>
                <TableBody>
                  {sortedData.map((row, index) => (
                    <TableRow
                      key={`${row.issuerTicker}-${index}`}
                      className="styled-row is-bordered is-border-top is-rounded bg-primary"
                    >
                      {MANAGER_HOLDINGS_TABLE_COLUMNS.map((column) => (
                        <TableCell
                          key={column.id}
                          title={column.getTitle?.(row)}
                          className={classnames(column.className, column.getColumnClassName?.(row))}
                          align={column.align}
                          isSticky={column.id === HoldingColumnEnum.ticker}
                          hasLeading={false}
                        >
                          {column.renderColumn(row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableStickyColumnWrapper>
          </div>
          <div className="fv-container has-responsive-padding flex w-full items-center justify-center overflow-hidden">
            <Pagination
              canGoBack={currentPage > 1}
              canGoForward={currentPage < totalPages}
              canGoToPage={(page) => page <= totalPages}
              visiblePages={stepNavigation}
              currentPage={currentPage}
              isLoading={managerHoldingsData.isFetching}
              getPageItemButtonProps={(pageNumber) => ({
                as: Link,
                to: insiderTradingRouter.getInsiderTradingLink({
                  view: InsiderView.ManagerHoldings,
                  params: { ...insiderTradingRouter.viewParams, page: pageNumber },
                }),
              })}
            />
          </div>
        </>
      )}
    </ManagerDetailsLayout>
  )
}

export default withQueryClientContext(ManagerHoldings)
